import { useEffect, useState } from 'react';
import { Network } from '@capacitor/network';
import { isNativePlatform } from '../lib/runtime';

export type ConnectivityState = {
  /** Device thinks it has a network path (not proof the API is up). */
  connected: boolean;
  ready: boolean;
};

/**
 * Network plugin + online/offline events.
 * Do not treat connected=true as API reachability.
 */
export function useConnectivity(): ConnectivityState {
  const [connected, setConnected] = useState(true);
  const [ready, setReady] = useState(!isNativePlatform());

  useEffect(() => {
    let remove: (() => void) | undefined;
    let cancelled = false;

    const apply = (next: boolean) => {
      if (!cancelled) setConnected(next);
    };

    (async () => {
      if (isNativePlatform()) {
        try {
          const status = await Network.getStatus();
          apply(status.connected);
          const listener = await Network.addListener('networkStatusChange', (s) => {
            apply(s.connected);
          });
          remove = () => listener.remove();
        } catch {
          apply(typeof navigator !== 'undefined' ? navigator.onLine : true);
        }
      } else {
        apply(typeof navigator !== 'undefined' ? navigator.onLine : true);
        const on = () => apply(true);
        const off = () => apply(false);
        window.addEventListener('online', on);
        window.addEventListener('offline', off);
        remove = () => {
          window.removeEventListener('online', on);
          window.removeEventListener('offline', off);
        };
      }
      if (!cancelled) setReady(true);
    })();

    return () => {
      cancelled = true;
      remove?.();
    };
  }, []);

  return { connected, ready };
}
